import type { ItemFieldRow } from './item-field-reader';
import { isOwnedRuleWindow } from './owned-rule-window';
import { createFormulaReadContext } from './formula-read-context';
import { createAbilityConditionEvaluator } from './ability-conditions';
import type { AbilityConditionDependencies } from './ability-conditions';
import { createInventoryReadContext } from './inventory-read-context';
import { readAbilityEntryId, containsAbilityTriggerKey } from './inventory-ability-grant-helpers';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { layerEntryNeedsResolve, resolveLayerEntry } from './layer-entry-resolver';
import { appendCompiledLayerInPlace } from './status-row-construction';
import { phpJsonString, phpJsonTruthy } from './php-json-values';

const arrayLike = (v: unknown): v is ItemFieldRow => v !== null && typeof v === 'object';
const own = (v: unknown, k: string): unknown => arrayLike(v) && Object.prototype.hasOwnProperty.call(v, k) ? (v as Record<string, unknown>)[k] : undefined;
const valid = (v: string): boolean => /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(v);
const upper = (v: string): string => v.replace(/[a-z]/g, c => c.toUpperCase());
function copy<T>(v: T): T {
    if (!arrayLike(v)) return v;
    const entries = Object.entries(v);
    return (entries.every(([key], i) => key === String(i)) ? entries.map(([, child]) => copy(child))
        : Object.fromEntries(entries.map(([k, child]) => [k, copy(child)]))) as T;
}
export interface NestedPassiveFoldDependencies extends AbilityConditionDependencies {
    queryFullItem: (id: string) => ItemFieldRow | null;
    isAura?: (row: ItemFieldRow) => boolean;
}
/** Native one-level membership fold. The existing bucket is snapshotted before
 * append; provenance prevents later re-entry from descending a folded child.
 */
export function createNestedPassiveCollector(deps: NestedPassiveFoldDependencies): (input: ItemFieldRow) => Record<string, unknown>[] {
    const emit = (e: readonly unknown[]): void => { deps.trace?.(e); };
    const reference = (id: string): ItemFieldRow | null => { emit(['reference', id]); return deps.resolveCarrierRef(id); };
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const common = {...deps, inventoryContext}, readers = createFormulaReadContext(common), conditions = createAbilityConditionEvaluator(common);
    const query = (id: string): ItemFieldRow | null => { emit(['full-item', id]); return deps.queryFullItem(id); };
    return input => {
        if (!arrayLike(input)) throw new TypeError('Passive fold requires a PHP array');
        const host = input, existing = new Set<string>();
        for (const entry of Object.values(readers.layerEntries(host, 'passive_ability'))) {
            let id = phpJsonString(readAbilityEntryId(entry, {...readers, resolveCarrierRef: reference}));
            if (id === '') id = readLayerListEntryUuid(entry);
            if (id !== '' && valid(id)) existing.add(upper(id));
        }
        const pending: Record<string, unknown>[] = [];
        const unwrap = (entry: unknown): unknown => arrayLike(own(entry, 'item_arr')) ? own(entry, 'item_arr') : entry;
        for (const outerEntry of Object.values(readers.layerEntries(host, 'passive_ability'))) {
            let source = own(outerEntry, 'source') ?? null;
            if (arrayLike(source)) source = own(source, 'source') ?? null;
            const origin = arrayLike(outerEntry) ? readers.itemField(outerEntry, 'built_from') : null;
            if (typeof source === 'string' && existing.has(upper(source))
                && origin !== 'user_choices' && origin !== 'add_ability_tab') continue;
            let outer = unwrap(outerEntry);
            if (layerEntryNeedsResolve(outer)) outer = resolveLayerEntry(outer, host, query);
            if (!arrayLike(outer)) continue;
            const layers = own(outer, 'item_layer_types');
            if ([...(arrayLike(layers) ? Object.keys(layers) : []), ...Object.keys(outer)].some(key => key.startsWith('if_'))
                || !conditions.check(outer, host, false, false)) continue;
            if (deps.isAura?.(outer)) continue;
            const outerId = phpJsonString(readers.itemField(outer, 'item_id') ?? '');
            for (const innerEntry of Object.values(readers.layerEntries(outer, 'passive_ability'))) {
                let inner = unwrap(innerEntry);
                if (layerEntryNeedsResolve(inner)) inner = resolveLayerEntry(inner, host, query);
                if (!arrayLike(inner)) continue;
                const id = phpJsonString(readers.itemField(inner, 'item_id') ?? '');
                if (id === '' || !valid(id) || existing.has(upper(id))) continue;
                const roll = readers.layerValue(inner, 'roll_type') !== null || phpJsonTruthy(readers.layerEntries(inner, 'roll_type'));
                if (roll && !containsAbilityTriggerKey(inner)) continue;
                existing.add(upper(id));
                pending.push({ability_id: id, item_name: readers.itemField(inner, 'item_name') ?? 'Unknown', source: outerId !== '' ? outerId : 'NA', requires_level: 1, item_arr: inner});
            }
        }
        return pending;
    };
}

export function createNestedPassiveFolder(deps: NestedPassiveFoldDependencies): (input: ItemFieldRow) => ItemFieldRow {
    const collect = createNestedPassiveCollector(deps);
    return input => {
        const host = (isOwnedRuleWindow(input) ? input : copy(input)) as Record<string, unknown>;
        for (const row of collect(host)) appendCompiledLayerInPlace(host, 'passive_ability', row);
        return host;
    };
}
